import { useQuery } from '@apollo/client';
import { Typography } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import type { PublicCollectionData } from '@mogul/api-client';
import { NATIVE_CURRENCY_ADDRESS } from '@mogul/shared';
// import axios from 'axios';
import { ethers } from 'ethers';
import type { FC } from 'react';
import { useContext, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';

import { ReactComponent as EtherscanIcon } from '../../common/svg/button-icons/etherscan-logo.svg';
import { ReactComponent as IpfsIcon } from '../../common/svg/button-icons/ipfs-logo.svg';
import { ReactComponent as ExternalLinkIcon } from '../../common/svg/general/icon-external-link.svg';
import { ReactComponent as DistributionIcon } from '../../common/svg/nft-action-card/distribution-icon.svg';
import { CoinGeckoContext } from '../../contexts/app-state-context/coingecko/coingecko.context';
import { ModalStateContext } from '../../contexts/app-state-context/modal-state.context';
import { MoralisContext } from '../../contexts/app-state-context/moralis/moralis.context';
import { useWeb3State } from '../../hooks';
import { useMarketplaceContract } from '../../hooks/marketplace-contract.hooks';
import type { Nft } from '../../hooks/nft-api.hook';
import { CHAIN_ID_TO_MORALIS, WEB3_CONFIG } from '../../lib';
import {
  getIsEthListing,
  getIsStarsListing,
  getListingEthPrice,
  getListingStarsPrice,
} from '../../lib/graph/graph-utils';
import type {
  Asset,
  Collection,
  Listing,
} from '../../lib/graph/MogulMarketplace';
import { AssetType, ListingType } from '../../lib/graph/MogulMarketplace';
import assetFullQuery from '../../lib/graph/queries/assetFullQuery';
import collectionQuery from '../../lib/graph/queries/collectionQuery';
import { translateIpfsToIpfsio } from '../../lib/nfts/nft-catalog.service';
import {
  getNetworkCurrencySymbol,
  getStarsListingTokenPrice,
  isEqualAddresses,
} from '../../lib/smart-contract-utils';
import { CountdownCard } from '../common/countdownCard/CountdownCard.component';
import { ConnectWalletModal } from '../common/modal';
import { MakeNftOrderModal } from '../common/modal/make-nft-order/MakeNftOrderModal.component';
import { BidAuctionModal } from '../common/modal/sell-auction-nft/BidAuctionModal.component';
import { OwnersModal } from '../common/modal/sell-auction-nft/OwnerModal.component';
import { PurchaseNFTModal } from '../common/modal/sell-auction-nft/PurchaseNFTModal.component';
import { SellAuctionModal } from '../common/modal/sell-auction-nft/SellAuctionModal.component';
import { TransferNFTModal } from '../common/modal/sell-auction-nft/TransferNFTModal.component';
import { SocialShareDropdown } from '../common/socialShareDropdown/socialShareDropdown.component';
import { Button } from '../core/button/Button';
import { UnlockableContent } from '../unlockable-content/UnlockableContent.component';
import styles from './NftActionCard.module.css';

interface FullAsset extends Asset {
  // currentOwners?: AccountAsset[];
  // allOwners?: AccountAsset[];
  isWhitelisted: boolean;
  isWhitelistAddress: boolean;
  isWhitelistDate: boolean;
  setFloorPrice: (floorPrice?: string) => void;
  bestOrderPrice?: string;
  image?: string;
  dbNft?: Nft | null;
  collection?: PublicCollectionData | null;
  goToCollectionsPage?: () => void;
}

interface ListingModified extends Listing {
  usdPrice: number;
}

export const NFTActionCard: FC<FullAsset> = ({
  setFloorPrice,
  bestOrderPrice,
  image,
  dbNft,
  collection,
  goToCollectionsPage,
  ...asset
}) => {
  const [owned, setOwned] = useState('0');
  const [conPrice, setConPrice] = useState<string>();
  const { setModal } = useContext(ModalStateContext);
  const { walletDetails } = useWeb3State();
  const { moralis, initialized } = useContext(MoralisContext);
  const { starsUsdRate: starsToUsdRatio, ethUsdRate: ethToUsdRatio } =
    useContext(CoinGeckoContext);

  const marketplaceContract = useMarketplaceContract({
    type: asset.type ?? AssetType.Erc1155,
  });

  const { data, loading } = useQuery<{ asset: FullAsset }>(assetFullQuery, {
    pollInterval: 5000,
    variables: {
      assetId: asset.id,
      accountAsset: `${walletDetails.address?.toLowerCase()}/${asset.id}`,
    },
  });

  const { data: collectionData } = useQuery<{ collection: Collection }>(
    collectionQuery,
    { variables: { nftContractAddress: asset.tokenAddress as string } },
  );

  useEffect(() => {
    if (
      !initialized ||
      !walletDetails.address ||
      walletDetails.chain.id !== WEB3_CONFIG.chains.bsc.id
    ) {
      return;
    }

    void (async () => {
      //  TODO Implement ETH / Rinkbey
      const nfts = await moralis.Web3API.account.getNFTsForContract({
        address: walletDetails.address!,
        token_address: asset.tokenAddress,
        chain: CHAIN_ID_TO_MORALIS[WEB3_CONFIG.chains.bsc.id],
      });

      const thisNft = nfts.result?.filter(
        i =>
          i.token_address === asset.tokenAddress &&
          i.token_id === String(asset.tokenId),
      )?.[0];

      if (!thisNft?.amount) {
        return;
      }

      setOwned(thisNft.amount);
    })();
  }, [moralis, initialized, walletDetails.address, walletDetails.chain.id]);

  const ownerAddress = data?.asset.owners
    ? (data.asset.owners.filter(owner => owner.quantity > 0)[0]?.account
        ?.address as string)
    : '';

  const explorerUrl = `${walletDetails.chain.explorerUrl}/token/${
    asset.tokenAddress as string
  }?a=${asset.tokenId}`;

  const ownerExplorerUrl = `${walletDetails.chain.explorerUrl}/address/${ownerAddress}`;

  const ipfsUrl = translateIpfsToIpfsio(asset.uri);

  const activeListings =
    data?.asset?.activeListings != null &&
    data?.asset?.activeListings.length > 0
      ? data?.asset?.activeListings
      : [];

  // ListingModified[]
  const activeListingsSort = activeListings.map(l => ({ ...l, usdPrice: 0 }));

  activeListingsSort.forEach(listing => {
    const ethPrice = getListingEthPrice(listing);
    const starsPrice = getListingStarsPrice(listing);
    if (listing.type === ListingType.Auction) {
      const isEthListing = getIsEthListing(listing);
      // multiplying by wei amount
      listing['usdPrice'] = isEthListing
        ? listing.highestBidAmount !== undefined
          ? listing.highestBidAmount * ethToUsdRatio
          : ethPrice * ethToUsdRatio
        : listing.highestBidAmount !== undefined
        ? listing.highestBidAmount * starsToUsdRatio
        : starsPrice * starsToUsdRatio;
    } else if (walletDetails.chain.id === WEB3_CONFIG.chains.eth.id) {
      listing['usdPrice'] = ethPrice * ethToUsdRatio;
    } else {
      listing['usdPrice'] = starsPrice * starsToUsdRatio;
    }
  });

  activeListingsSort.sort((a, b) => a.usdPrice - b.usdPrice);

  const activeListing = activeListingsSort.filter(listing => {
    const isActiveAuction =
      listing.type === ListingType.Auction &&
      listing.endTime > Date.now() / 1000;

    const isListing = listing.type === ListingType.Listing;

    return isActiveAuction || isListing;
  })?.[0];

  const isStarsListing = useMemo(
    () => !!activeListing && getIsStarsListing(activeListing),
    [activeListing],
  );
  const isEthListing = useMemo(
    () => !!activeListing && getIsEthListing(activeListing),
    [activeListing],
  );
  const listingStarsPrice = useMemo(
    () => !!activeListing && getListingStarsPrice(activeListing),
    [activeListing],
  );
  const listingEthPrice = useMemo(
    () => !!activeListing && getListingEthPrice(activeListing),
    [activeListing],
  );

  const lowestPriceInUsd = activeListing
    ? // ? isEthActiveListing
      isEthListing
      ? (
          parseFloat(
            ethers.utils.formatEther(
              conPrice ??
                activeListing.highestBidAmount ??
                // activeListing.ethPrice,
                listingEthPrice,
            ),
          ) * ethToUsdRatio
        ).toLocaleString('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }) + ' USD'
      : // : activeListing.isStarsListing
      isStarsListing
      ? (
          parseInt(
            ethers.utils.formatEther(
              conPrice ??
                activeListing.highestBidAmount ??
                // activeListing.starsPrice,
                listingStarsPrice,
            ),
            10,
          ) * starsToUsdRatio
        ).toLocaleString('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }) + ' USD'
      : 0
    : 0;

  const ownedQuantity = walletDetails.address
    ? data?.asset?.owners?.reduce((count, owner) => {
        const isEqual = isEqualAddresses(
          owner?.account?.address ?? '',
          walletDetails.address ?? '',
        );

        return isEqual ? count + owner.quantity : count;
      }, 0) ?? 0
    : 0;

  const hasSupply = ownedQuantity > 0;

  const timerText = activeListing
    ? activeListing.startTime > Date.now() / 1000
      ? 'Starts in'
      : activeListing.startTime < Date.now() / 1000 &&
        activeListing.endTime > Date.now() / 1000
      ? 'Ends in'
      : 'Auction Ended'
    : '';

  useEffect(() => {
    if (!activeListing && !asset.type && !marketplaceContract.initialized) {
      return;
    }

    void (async () => {
      if (!activeListing) {
        return;
      }
      if (activeListing.type === ListingType.Listing) {
        const listing = await marketplaceContract.getListingById(
          activeListing.listingId as string,
        );
        if (!listing) {
          return;
        }

        if (isEthListing) {
          const ethListingTokenIndex = listing.listingTokens.findIndex(
            listingToken => listingToken === NATIVE_CURRENCY_ADDRESS,
          );
          const listingPriceEth = listing.listingPrices[ethListingTokenIndex];
          setConPrice(listingPriceEth.toString());
        } else {
          const listingPriceStars = getStarsListingTokenPrice(
            listing.listingTokens,
            listing.listingPrices,
          );
          setConPrice(listingPriceStars?.toString());
        }
      } else {
        const auction = await marketplaceContract.getAuctionById(
          activeListing.listingId as string,
        );
        const highestBid = auction?.highestBid.amount.toString();
        const startingPrice = auction?.startingPrice.toString();
        const startingBidPrice =
          highestBid !== undefined && highestBid !== '0'
            ? highestBid
            : startingPrice ?? '0';
        setConPrice(startingBidPrice);
      }
    })();
  }, [marketplaceContract, asset, activeListing]);

  // const currentStarsPrice = activeListing?.isStarsListing
  const currentStarsPrice = isStarsListing
    ? ethers.utils.formatEther(
        conPrice ??
          activeListing?.highestBidAmount ??
          // activeListing.starsPrice,
          listingStarsPrice,
      )
    : undefined;
  // const currentEthPrice = activeListing?.isEthListing
  const currentEthPrice = isEthListing
    ? ethers.utils
        .formatEther(
          conPrice ??
            activeListing?.highestBidAmount ??
            // activeListing.ethPrice,
            listingStarsPrice,
        )
        .toString() + ` ${getNetworkCurrencySymbol()}`
    : undefined;

  useEffect(() => {
    setFloorPrice(currentStarsPrice);
  }, [setFloorPrice, currentStarsPrice]);

  // needed so typescript doesn't freak out
  let dummyListing: Listing;

  const makeNftOrderOnClick = () => {
    setModal(
      MakeNftOrderModal({
        asset,
        ownedQuantity,
        floorPrice: currentStarsPrice,
        bestOffer: bestOrderPrice,
        image,
        dbNft,
        collection,
        goToCollectionsPage,
      }),
    );
  };
  const sellNftOnClick = () => {
    setModal(SellAuctionModal({ asset, listing: activeListing as Listing }));
  };
  const transferOnClick = () => {
    setModal(TransferNFTModal({ asset }), false, styles.transferDialog);
  };
  const purchaseNftOnClick = () => {
    if (!walletDetails.address) {
      setModal(ConnectWalletModal());
    } else {
      // if (asset.isWhitelisted && !asset.isWhitelistAddress) {
      //   toast.error('Wallet address is not eligible for whitelist sale');
      //   return;
      // }
      setModal(
        PurchaseNFTModal({
          isWhitelisted: asset.isWhitelisted,
          isWhitelistAddress: asset.isWhitelistAddress,
          listing: activeListing ?? dummyListing,
        }),
      );
    }
  };
  const BidAuctionOnClick = () => {
    if (!walletDetails.address) {
      setModal(ConnectWalletModal());
    } else {
      if (asset.isWhitelisted && !asset.isWhitelistAddress) {
        toast.error('Wallet address is not eligible for whitelist sale');
        return;
      }

      setModal(
        BidAuctionModal({
          isWhitelisted: asset.isWhitelisted,
          isWhitelistAddress: asset.isWhitelistAddress,
          listing: activeListing ?? dummyListing,
        }),
      );
    }
  };

  const initListingBtns = (
    <div className={`my-5 grid gap-4 grid-col-4 grid-row-1 w-full`}>
      <Button
        kind="secondary"
        className="w-full h-14"
        onClick={transferOnClick}
      >
        Transfer
      </Button>
      <Button
        className="w-full h-14 col-start-2 col-end-4"
        onClick={sellNftOnClick}
      >
        SELL NFT
      </Button>
    </div>
  );

  const initPurchaseBtns = (
    <>
      <div className="flex flex-col text-center w-full">
        {activeListing && (
          <div>
            <div className={`${styles.priceMain}`}>
              {currentEthPrice ?? `${currentStarsPrice} MOGUL`}
            </div>
            <div className={`${styles.priceUSD}`}>{lowestPriceInUsd}</div>
          </div>
        )}
      </div>

      {activeListing?.type === 'LISTING' &&
      activeListing.seller.address !== walletDetails.address?.toLowerCase() ? (
        <div className={`my-5 w-full`}>
          <Button
            className="w-full h-14 col-start-2 col-end-4"
            onClick={purchaseNftOnClick}
            disabled={
              Boolean(walletDetails.address) &&
              (asset.isWhitelisted ? !asset.isWhitelistAddress : false)
            }
          >
            Purchase NFT
          </Button>
        </div>
      ) : (
        activeListing?.type === 'AUCTION' &&
        activeListing.seller.address !==
          walletDetails.address?.toLowerCase() && (
          <div className={`my-5 w-full`}>
            <Button
              className="w-full h-14 col-start-2 col-end-4"
              onClick={BidAuctionOnClick}
              disabled={
                activeListing.startTime > Date.now() / 1000 ||
                (Boolean(walletDetails.address) &&
                  (asset.isWhitelisted ? !asset.isWhitelistAddress : false))
              }
            >
              Place Bid
            </Button>
          </div>
        )
      )}
    </>
  );

  const hasListingOrAuction = (
    <div className="w-full flex flex-col items-center">
      <div className="flex flex-col w-11/12">
        {activeListing && initPurchaseBtns}
      </div>
      {activeListing?.seller.address !== walletDetails.address?.toLowerCase() &&
        asset.isWhitelisted && (
          <div className={`${styles.collection} pb-2`}>
            {!asset.isWhitelistDate ? (
              'Whitelist sale has not started yet.'
            ) : !asset.isWhitelistAddress && Boolean(walletDetails.address) ? (
              <div className="flex flex-col items-center">
                <p>Wallet not eligible for whitelist sale.</p>
                <br />
                <p>
                  Find out more about whitelist eligibility on{' '}
                  <Typography
                    component="span"
                    color="primary"
                    style={{ color: 'gold', fontWeight: 'bold' }}
                  >
                    <a
                      target="_blank"
                      rel="noreferrer"
                      href="https://discord.com/invite/MogulProductions"
                      className={
                        'hover:underline focus:hover:underline-offset-2'
                      }
                    >
                      Discord
                    </a>{' '}
                  </Typography>
                  or{' '}
                  <Typography
                    component="span"
                    color="primary"
                    style={{ color: 'gold', fontWeight: 'bold' }}
                  >
                    <a
                      target="_blank"
                      rel="noreferrer"
                      href="https://twitter.com/mogulofficial_"
                      className={
                        'hover:underline focus:hover:underline-offset-2'
                      }
                    >
                      Twitter
                    </a>
                  </Typography>
                </p>
              </div>
            ) : (
              ''
            )}
          </div>
        )}
    </div>
  );

  const hasListing = (
    <div className="w-full flex flex-col items-center">
      <div className="flex w-11/12">{hasSupply && initListingBtns}</div>
    </div>
  );

  const generalInfo = (
    <div className="flex flex-col w-full pt-1 items-center">
      <div className="flex w-11/12">
        {loading || !ownerAddress ? (
          <div className="flex w-full justify-center">
            <Skeleton variant="rect" height={60} width={200} />
          </div>
        ) : asset.totalSupply === 1 ? (
          <div className={`mt-5 mb-1 flex flex-col w-full justify-between`}>
            <Typography
              variant="subtitle1"
              style={{ opacity: 0.5, color: '#FFFFFF' }}
            >
              Owner
            </Typography>
            <div className="flex flex-row items-center">
              <a
                className="cursor-pointer flex flex-row"
                href={ownerExplorerUrl}
                target="_blank"
                rel="noreferrer"
              >
                <Typography
                  variant="h1"
                  color="primary"
                  style={{ color: 'gold', fontWeight: 'bold' }}
                >
                  <div
                    className={`${styles.addressFont}`}
                  >{`${ownerAddress.slice(0, 9)}...${ownerAddress.slice(
                    ownerAddress.length - 15,
                  )}`}</div>
                </Typography>
                <ExternalLinkIcon className={`${styles.exploreIcon} ml-3`} />
              </a>
            </div>
          </div>
        ) : (
          <div className={`mt-5 mb-1 flex flex-row w-full justify-between`}>
            <Typography
              onClick={() => setModal(OwnersModal({ asset }))}
              className="flex flex-row cursor-pointer hover:opacity-100 opacity-50"
              variant="subtitle1"
              style={{ color: '#FFFFFF' }}
            >
              {asset.numOwners} Owner{(asset.numOwners ?? 0) > 1 ? 's' : ''}
            </Typography>
            <Typography
              className="flex flex-row"
              variant="subtitle1"
              style={{ opacity: 0.5, color: '#FFFFFF' }}
            >
              {asset.totalSupply} Total
            </Typography>
            <Typography
              className="flex flex-row"
              variant="subtitle1"
              style={{ opacity: 0.5, color: '#FFFFFF' }}
            >
              You own {ownedQuantity}
            </Typography>
          </div>
        )}
      </div>
    </div>
  );

  const countdownButtonReplacement = (
    <div
      className={`${styles.buttonReplacementContainer} flex flex-col items-center w-full py-4`}
    >
      <div className="w-11/12">
        <div
          className={`${styles.buttonReplacementBg} w-full h-10 rounded-full lg:mt-0`}
        >
          <DistributionIcon className="mr-1" />{' '}
          {(asset.commission?.commissionBasisPoint ??
            collectionData?.collection?.commission?.commissionBasisPoint ??
            0) / 100}
          % of sales go to creator
        </div>
      </div>
    </div>
  );

  const ExternalElements = (
    <div className=" w-full grid gap-16 grid-cols-1 grid-rows-3 h-28 md:gap-4 md:grid-cols-3 md:grid-rows-1 md:h-5">
      <a
        target="_blank"
        className={`mt-3 py-2 w-full flex 2xl:text-lg lg:text-base text-sm justify-center items-center cursor-pointer rounded h-10 ${styles.bg}`}
        href={explorerUrl}
        rel="noreferrer"
      >
        <EtherscanIcon className="ml-3 mr-3" />
        View on Explorer
      </a>
      {/* <a
        target="_blank"
        className={`mt-3 py-2 w-full flex 2xl:text-lg lg:text-base text-sm justify-center items-center cursor-pointer rounded h-10 ${styles.bg}`}
        href={openSeaUrl}
        rel="noreferrer"
      >
        <OpenSeaIcon className="ml-3 mr-3" />
        View on OpenSea
      </a> */}
      <a
        target="_blank"
        className={`mt-3 py-2 w-full flex 2xl:text-lg lg:text-base text-sm justify-center items-center cursor-pointer rounded h-10 ${styles.bg}`}
        href={ipfsUrl}
        rel="noreferrer"
      >
        <IpfsIcon className="ml-3 mr-3" />
        View on IPFS
      </a>

      <SocialShareDropdown
        url="{`placeholder`}"
        styles={`mt-3 py-2 w-full flex 2xl:text-lg lg:text-base text-sm justify-center items-center cursor-pointer rounded h-10 ${styles.bg}`}
        buttonStyle={{ color: 'white' }}
      />
    </div>
  );

  return (
    <div className={`w-full`}>
      <CountdownCard
        startTime={activeListing?.startTime ?? 0}
        endTime={activeListing?.endTime ?? 0}
        onlyTimerText={
          activeListing ? activeListing.startTime > Date.now() / 1000 : false
        }
        timerText={timerText}
        showTimer={activeListing?.type === ListingType.Auction}
        toolTipText={'Auction timer'}
        countdownButtonReplacement={countdownButtonReplacement}
        externalElements={ExternalElements}
      >
        <>
          {generalInfo}
          {hasListingOrAuction}
          {hasListing}
          <Button
            className="w-11/12 h-14 col-start-2 col-end-4"
            onClick={makeNftOrderOnClick}
            disabled={!walletDetails.address}
          >
            Make offer
          </Button>
          <div className="flex w-11/12">
            <UnlockableContent
              nftContractAddress={asset.tokenAddress as string}
              nftId={asset.tokenId.toString()}
            />
          </div>
          {countdownButtonReplacement}
        </>
      </CountdownCard>
    </div>
  );
};
